import { SettingsElementType, } from '@thoughtspot/ts-chart-sdk';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import { isPropElement } from './custom-chart-schema-hydrator.util';
import { generateVisualPropsFromElements } from './custom-visual-props';
const SINGLE_VALUE_ELEMENTS = [
    SettingsElementType.RADIO_GROUP,
];
export const getElementValue = (element) => {
    if (isPropElement(element)) {
        const elementType = element.type;
        if (elementType !== 'section' && elementType !== 'customsort') {
            return element?.defaultValue;
        }
        return '';
    }
    switch (element.elementType) {
        case SettingsElementType.RADIO_GROUP:
            return (element?.properties?.selectedValue ??
                element?.properties?.defaultValue);
        case SettingsElementType.DROPDOWN:
            return element?.properties?.selectedOptions;
        case SettingsElementType.COLOR_PICKER:
            return element?.properties?.color ?? element?.properties?.value;
        case SettingsElementType.TOGGLE:
        case SettingsElementType.RADIO:
            return element?.properties?.checked;
        default:
            return element?.properties?.value;
    }
};
export const sanitizeSettingsElementsArray = (currentVisualProps, elements, globalPersistedVisualPropKeys) => {
    const visualProps = {};
    const processElement = (element) => {
        if (element?.children &&
            element?.children.length &&
            !SINGLE_VALUE_ELEMENTS.includes(element.elementType)) {
            if (element.key) {
                const nestedResult = sanitizeSettingsElementsArray(currentVisualProps?.[element.key], element.children, globalPersistedVisualPropKeys);
                const existingValue = _.get(visualProps, element.key);
                if (existingValue &&
                    _.isObject(existingValue) &&
                    _.isObject(nestedResult)) {
                    _.set(visualProps, element.key, {
                        ...existingValue,
                        ...nestedResult,
                    });
                }
                else {
                    _.set(visualProps, element.key, nestedResult);
                }
            }
            else {
                element.children.forEach(processElement);
            }
        }
        else if (element.key) {
            const defaultValue = getElementValue(element);
            const currentValue = currentVisualProps?.[element.key];
            const shouldUseDefault = currentValue === undefined ||
                currentValue === null ||
                (_.isObject(currentValue) && _.isEmpty(currentValue));
            _.set(visualProps, element.key, shouldUseDefault ? defaultValue : currentValue);
        }
    };
    elements.forEach(processElement);
    const currentVisualPropsWithPreservedState = currentVisualProps?.clientState
        ? {
            ...visualProps,
            clientState: DOMPurify.sanitize(currentVisualProps.clientState),
        }
        : visualProps;
    if (!globalPersistedVisualPropKeys ||
        _.isEmpty(globalPersistedVisualPropKeys)) {
        return currentVisualPropsWithPreservedState;
    }
    globalPersistedVisualPropKeys.forEach(value => {
        if (_.get(currentVisualProps, value)) {
            _.set(currentVisualPropsWithPreservedState, value, DOMPurify.sanitize(_.get(currentVisualProps, value)));
        }
    });
    return currentVisualPropsWithPreservedState;
};
export const sanitizeCustomVisualPropsV2 = (customVisualProps, visualPropEditorDefinition, globalPersistedVisualPropKeys) => {
    let sanitizedVisualProps = {
        columnVisualProps: {},
        axisVisualProps: {},
        dataLabelVisualProps: {},
        tooltipVisualProps: {},
        legendVisualProps: {},
        displayVisualProps: {},
    };
    const { elements, columnsVizPropDefinition, displayVizPropDefinition, dataLabelVizPropDefinition, tooltipVizPropDefinition, legendVizPropDefinition, axisVizPropDefinition, } = visualPropEditorDefinition;
    if (columnsVizPropDefinition && columnsVizPropDefinition.length) {
        sanitizedVisualProps.columnVisualProps = {};
        columnsVizPropDefinition.forEach(item => {
            Object.entries(item.columnSettingsDefinition).forEach(([colId, colDef]) => {
                sanitizedVisualProps.columnVisualProps[colId] = sanitizeSettingsElementsArray(customVisualProps?.columnVisualProps?.[colId] ?? {}, (colDef?.elements || []), globalPersistedVisualPropKeys);
            });
        });
    }
    if (axisVizPropDefinition && axisVizPropDefinition.length) {
        sanitizedVisualProps.axisVisualProps = sanitizeSettingsElementsArray(customVisualProps?.axisVisualProps ?? {}, axisVizPropDefinition, globalPersistedVisualPropKeys);
    }
    if (dataLabelVizPropDefinition && dataLabelVizPropDefinition.length) {
        sanitizedVisualProps.dataLabelVisualProps = sanitizeSettingsElementsArray(customVisualProps?.dataLabelVisualProps ?? {}, dataLabelVizPropDefinition, globalPersistedVisualPropKeys);
    }
    if (tooltipVizPropDefinition && tooltipVizPropDefinition.length) {
        sanitizedVisualProps.tooltipVisualProps = sanitizeSettingsElementsArray(customVisualProps?.tooltipVisualProps ?? {}, tooltipVizPropDefinition, globalPersistedVisualPropKeys);
    }
    if (legendVizPropDefinition && legendVizPropDefinition.length) {
        sanitizedVisualProps.legendVisualProps = sanitizeSettingsElementsArray(customVisualProps?.legendVisualProps ?? {}, legendVizPropDefinition, globalPersistedVisualPropKeys);
    }
    if (displayVizPropDefinition && displayVizPropDefinition.length) {
        sanitizedVisualProps.displayVisualProps = sanitizeSettingsElementsArray(customVisualProps?.displayVisualProps ?? {}, displayVizPropDefinition, globalPersistedVisualPropKeys);
    }
    if (customVisualProps?.clientState) {
        sanitizedVisualProps.clientState = DOMPurify.sanitize(customVisualProps.clientState);
    }
    if (elements && elements.length) {
        sanitizedVisualProps = {
            ...sanitizedVisualProps,
            ...generateVisualPropsFromElements(elements),
        };
    }
    if (globalPersistedVisualPropKeys &&
        !_.isEmpty(globalPersistedVisualPropKeys)) {
        globalPersistedVisualPropKeys.forEach(key => {
            const value = _.get(customVisualProps, key);
            if (value !== undefined) {
                _.set(sanitizedVisualProps, key, DOMPurify.sanitize(value));
            }
        });
    }
    return sanitizedVisualProps;
};
//# sourceMappingURL=helpers.js.map